/**
 * Accordion V2 — smooth animated accordion
 * Emil Kowalski principles:
 *   - grid-template-rows 0fr→1fr for hardware-accelerated height animation
 *   - cubic-bezier(0.23, 1, 0.32, 1) strong ease-out
 *   - data-ready gates transitions (no animation on initial render)
 *   - asymmetric: opening 220ms, closing is faster via opacity 180ms
 */

class AccordionV2 extends HTMLElement {
  #controller = new AbortController();

  get #details() {
    return this.querySelector('details');
  }

  get #summary() {
    return this.#details.querySelector('summary');
  }

  connectedCallback() {
    const { signal } = this.#controller;
    const details = this.#details;

    // Set initial state without triggering any transition
    details.dataset.state = details.open ? 'open' : 'closed';

    // Enable transitions only after initial render to avoid page-load jank
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        details.dataset.ready = '';
      });
    });

    this.#summary.addEventListener('click', this.#handleClick, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #handleClick = (event) => {
    event.preventDefault();
    const details = this.#details;

    if (details.open) {
      this.#close(details);
    } else {
      this.#open(details);
    }
  };

  #open(details) {
    // "opening" starts caret rotation immediately
    details.dataset.state = 'opening';
    details.open = true;

    // Double rAF: first frame sets open=true (content in DOM, grid still 0fr),
    // second frame triggers the grid transition
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        details.dataset.state = 'open';
      });
    });
  }

  #close(details) {
    details.dataset.state = 'closing';

    const body = details.querySelector('.details__body-v2');

    const finish = () => {
      if (details.dataset.state === 'closing') {
        details.open = false;
        details.dataset.state = 'closed';
      }
    };

    // Primary: transitionend on the body grid
    body.addEventListener('transitionend', finish, { once: true });

    // Fallback: in case transitionend doesn't fire (e.g. tab hidden)
    setTimeout(finish, 280);
  }
}

if (!customElements.get('accordion-v2')) {
  customElements.define('accordion-v2', AccordionV2);
}